import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/auth';
import { createLogger } from '../utils/logger';

const logger = createLogger('UserContext');

interface UserContextValue {
  user: User | null;
  loading: boolean;
  isPasswordRecovery: boolean;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser(true); // force refresh
      setUser(userData);
    } catch (error) {
      logger.error('Failed to refresh user:', error);
    }
  }, []);

  useEffect(() => {
    // Initial load with error handling
    const loadUser = async () => {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        logger.error('Failed to load current user:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUser();

    // Listen for auth state changes
    const { data: authListener } = authService.onAuthStateChange((currentUser, event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        setUser(currentUser); // keep session so updatePassword works
      } else {
        setIsPasswordRecovery(false);
        setUser(currentUser);
      }
      setLoading(false);
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  return (
    <UserContext.Provider value={{ user, loading, isPasswordRecovery, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
